import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Sparkles, ArrowLeft, Save, User, Palette, Globe, Bell, Mic } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    language: 'en',
    theme: 'dark',
    default_model: 'sonar',
    voice_enabled: false,
    notifications_enabled: true
  });
  const [languages, setLanguages] = useState({});

  useEffect(() => {
    fetchPreferences();
    fetchLanguages();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/user/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API}/languages`);
      setLanguages(response.data.languages);
    } catch (error) {
      console.error('Failed to fetch languages:', error);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/preferences`, preferences, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Preferences saved successfully');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const updatePreference = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin text-primary">
          <Sparkles size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-background to-black">
      <nav className="border-b border-border/50 backdrop-blur-md bg-card/30 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/search')} data-testid="back-button">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-2">
              <Sparkles className="text-primary" size={24} />
              <h1 className="text-xl font-heading font-bold text-foreground">Settings</h1>
            </div>
          </div>
          <Button onClick={savePreferences} disabled={saving} data-testid="save-settings-button">
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Profile Section */}
        <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="profile-section">
          <div className="flex items-center gap-3 mb-6">
            <User className="text-primary" size={24} />
            <h2 className="text-lg font-heading font-semibold text-foreground">Profile</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Name</label>
              <p className="text-foreground font-medium">{user?.full_name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Email</label>
              <p className="text-foreground">{user?.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Role</label>
              <span className="ml-2 text-xs px-2 py-1 rounded-full bg-primary/20 text-primary capitalize">
                {user?.role?.replace('_', ' ')}
              </span>
            </div>
          </div>
        </Card>

        {/* Appearance Section */}
        <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="appearance-section">
          <div className="flex items-center gap-3 mb-6">
            <Palette className="text-primary" size={24} />
            <h2 className="text-lg font-heading font-semibold text-foreground">Appearance</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Theme</label>
              <Select value={preferences.theme} onValueChange={(v) => updatePreference('theme', v)}>
                <SelectTrigger data-testid="theme-selector" className="w-full max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Language Section */}
        <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="language-section">
          <div className="flex items-center gap-3 mb-6">
            <Globe className="text-primary" size={24} />
            <h2 className="text-lg font-heading font-semibold text-foreground">Language</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Preferred Language</label>
              <Select value={preferences.language} onValueChange={(v) => updatePreference('language', v)}>
                <SelectTrigger data-testid="language-selector" className="w-full max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(languages).map(([code, name]) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                Search results will be translated to your preferred language
              </p>
            </div>
          </div>
        </Card>

        {/* AI Model Section */}
        <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="model-section">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="text-primary" size={24} />
            <h2 className="text-lg font-heading font-semibold text-foreground">AI Model</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Default Model</label>
              <Select value={preferences.default_model} onValueChange={(v) => updatePreference('default_model', v)}>
                <SelectTrigger data-testid="model-selector" className="w-full max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sonar">Sonar (Fast)</SelectItem>
                  <SelectItem value="gpt-5.2">GPT-5.2 (Premium)</SelectItem>
                  <SelectItem value="claude-sonnet-4.5">Claude Sonnet 4.5 (Premium)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                Premium models require admin access
              </p>
            </div>
          </div>
        </Card>

        {/* Voice & Notifications Section */}
        <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="features-section">
          <div className="flex items-center gap-3 mb-6">
            <Mic className="text-primary" size={24} />
            <h2 className="text-lg font-heading font-semibold text-foreground">Features</h2>
          </div>
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Voice Search</p>
                <p className="text-xs text-muted-foreground">Enable voice input for searches</p>
              </div>
              <Switch
                checked={preferences.voice_enabled}
                onCheckedChange={(v) => updatePreference('voice_enabled', v)}
                data-testid="voice-toggle"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Notifications</p>
                <p className="text-xs text-muted-foreground">Receive updates and alerts</p>
              </div>
              <Switch
                checked={preferences.notifications_enabled}
                onCheckedChange={(v) => updatePreference('notifications_enabled', v)}
                data-testid="notifications-toggle"
              />
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default SettingsPage;
